import styles from './index.module.css'

import { LuPlus } from '@react-icons/all-files/lu/LuPlus'
import React, { useCallback, useMemo, useState } from 'react'

import { filterFilters } from '@disco/utils/search-page'

import { FilterDefinition, FilterState } from 'types/filter-page'

import {
  Badge,
  Box,
  Button,
  DropdownMenu,
  Flex,
  SearchTextField,
  Text,
} from 'components/ds'
import HighlightedText from 'components/ui/highlighted-text'

type Props = {
  definitions: FilterDefinition[]
  currentFilterStates: FilterState[]
  onAddFilter: (_filter: FilterDefinition) => void
}

const highlightColors = {
  background: 'var(--disco-yellow)',
}

const AddFilter = ({
  definitions,
  currentFilterStates,
  onAddFilter,
}: Props) => {
  const [filterKeywords, setFilterKeywords] = useState('')

  const filters = useMemo(
    () =>
      filterFilters(filterKeywords, definitions)
        .filter(f => !f.fixed)
        // filter out filters that have no filter options to hide from dropdown
        .filter(f => !f.options || f.options.length > 0),
    [filterKeywords, definitions],
  )

  const isFilterSelected = useCallback(
    (filter: FilterDefinition) => {
      const isRootFilterSelected = currentFilterStates.some(
        state => state.definition.label === filter.label,
      )
      const isNestedFilterSelected = currentFilterStates.some(state =>
        state.definition.options?.some(option => option.label === filter.label),
      )
      return isRootFilterSelected || isNestedFilterSelected
    },
    [currentFilterStates],
  )

  // Hint that a newly added filter is available. Stays until `isNew` is dropped.
  const hasNewFilter = definitions.some(definition => definition.isNew)

  return (
    <DropdownMenu.Root modal={false}>
      <DropdownMenu.Trigger>
        <Button highContrast color="gray" variant="soft">
          <LuPlus />
          <Text as="span" size={{ initial: '1', sm: '2' }}>
            Filter
          </Text>
          {hasNewFilter && (
            <Box className={styles.newDot} data-testid="filter-new-dot" />
          )}
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content variant="soft">
        <div
          onKeyDown={e => {
            e.stopPropagation()
          }}
          onKeyUp={e => {
            e.stopPropagation()
          }}
        >
          <SearchTextField
            mb="2"
            placeholder="Search filters"
            onChange={e => setFilterKeywords(e.target.value)}
            value={filterKeywords}
          />
        </div>
        {filters.map(filter =>
          filter.options ? (
            <DropdownMenu.Sub key={filter.label}>
              <DropdownMenu.SubTrigger
                onPointerEnter={e => {
                  // Force immediate open on hover
                  const target = e.currentTarget as HTMLElement
                  target.click()
                }}
              >
                <HighlightedText
                  text={filter.label}
                  highlights={[filterKeywords]}
                  customColors={highlightColors}
                />
              </DropdownMenu.SubTrigger>
              <DropdownMenu.SubContent>
                {filter.options.map(option => (
                  <DropdownMenu.Item
                    key={option.field}
                    onSelect={() => onAddFilter(option)}
                    disabled={isFilterSelected(option)}
                  >
                    <HighlightedText
                      text={option.label}
                      highlights={[filterKeywords]}
                      customColors={highlightColors}
                    />
                  </DropdownMenu.Item>
                ))}
              </DropdownMenu.SubContent>
            </DropdownMenu.Sub>
          ) : (
            <DropdownMenu.Item
              key={filter.label}
              onSelect={() => onAddFilter(filter)}
              disabled={isFilterSelected(filter)}
            >
              <Flex align="center" gap="2">
                <HighlightedText
                  text={filter.label}
                  highlights={[filterKeywords]}
                  customColors={highlightColors}
                />
                {filter.isNew && (
                  <Badge color="blue" radius="full" size="1">
                    NEW
                  </Badge>
                )}
              </Flex>
            </DropdownMenu.Item>
          ),
        )}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  )
}

export default AddFilter
